/**
 * 報價單「單價」計算的唯一定義（需求 R132）。
 *
 * ── 這支模組為什麼存在
 * calcTierPrice / roundPrice / getBaseCostFromSource / calcUnitPrice 過去在
 * QuoteCreatePage.js 與 QuoteEditPage.js 各有一份**複製品**，兩份已經漂移過不只一次
 * （需求 R103 AC4 補 normalizeCostSource、需求 R132 查出編輯頁的 getBaseCostFromSource
 * 是裸的 `?.unitPrice ?? null`，連建立頁早就有的「箱價 ÷ 箱入數」補算都沒有 → 同一筆產品
 * 在兩頁會算出不同單價）。本檔把整條單價算式收斂成一份，兩頁 import 同一個實作，
 * 比照 utils/quoteMoney.js（需求 R111）、utils/quoteRemark.js（需求 R96）的做法。
 *
 * ── 需求 R132 修的是什麼
 * PM 2026-08-27 逐字：
 *   「單價的結果在台幣幣別的狀況下是『無條件進位到小數 1 位』，驗算結果：
 *     1階 59.85+1.045=60.895，無條件進位到小數 1 位是 60.9，為何變成 61.0?
 *     4階 59.85/0.92+1.045=66.0993，無條件進位到小數 1 位是 66.1，為何變成 66.2」
 *
 * 根因 A（主因）＝**同一個成本被無條件進位了兩次**。
 *   產品主檔存進 DB 的「標準成本 unitPrice」本身已先被進位到小數 1 位
 *   （src/server/utils/cost.js calcUnitPrice ＝ Math.ceil(箱價 ÷ 箱入數 × 10) / 10，
 *    匯入 productImportParser、手動 ProductModal、顯示層 productController 都走這一式）。
 *   以 PM 案例 B115001 為例：箱價 718.2 ÷ 12 入 ＝ 59.85，進 DB 時就被墊高成 59.9。
 *   報價單再拿這個「已進位值」跑階數公式、末端又進位一次 → 多墊的 0.05 只要讓真值
 *   跨過 0.1 邊界就會憑空多 0.1：
 *
 *     階 ｜ 真值(59.85)→進位 ｜ 舊系統(59.9)→進位 ｜ 結果
 *     1  ｜ 60.895   → 60.9  ｜ 60.945   → 61.0   ｜  多 0.1
 *     2  ｜ 62.4296  → 62.5  ｜ 62.4809  → 62.5   ｜  一致
 *     3  ｜ 64.045   → 64.1  ｜ 64.0976  → 64.1   ｜  一致
 *     4  ｜ 66.0993  → 66.1  ｜ 66.1537  → 66.2   ｜  多 0.1
 *
 *    2/3 階不是「本來就對」——只是這個品項的真值離 0.1 邊界較遠、剛好躲過。
 *      換一個品項，2/3 階一樣會錯。這是同一個根因的機率性外顯，不是四條獨立的 bug。
 *   修法（本檔 resolveEntryBaseCost）：報價單的成本基準改讀「箱價 ÷ 箱入數」的**未進位原值**，
 *   整條算式全程不中途進位，只在最終單價無條件進位一次（需求 R82 D4 不變）。
 *
 * 根因 B（獨立的第二個「多 0.1」來源）＝**roundPrice 缺浮點雜訊防線**。
 *   兩頁舊版都是裸的 Math.ceil(price * 10) / 10，沒有 server/utils/cost.js（需求 R70）與
 *   utils/quoteMoney.js ceilMoney（需求 R111）那樣先 Number((v * factor).toFixed(6))
 *   抹掉 1e-13 等級的二進位誤差。實測 11.4 / 0.95 === 12.000000000000002 → 舊版輸出 12.1，
 *   正確應為 12.0；標準成本 10.0～2000.0（每 0.1 一格）× 2/3/4 階的窮舉中共 418 組中招。
 *   修法：本檔 roundPrice 直接改呼叫 ceilMoney（USD/EUR 2 位、TWD/HKD 1 位，規則不變）。
 *
 * ── 與 需求 R70 的關係（重要，勿誤讀成推翻）
 * 需求 R70（PM 2026-07-19 拍板）「成本金額（箱價／單價）在顯示與儲存上都應為最多小數
 * 第一位、無條件進位」**維持不變**：產品管理列表／詳情／匯入／ProductModal 一律照舊顯示
 * 與儲存 59.9，本票一行都沒動 server/utils/cost.js、productImportParser.js、
 * productController.js、ProductModal.js。
 * 改的只有「報價單內部算式代入哪個成本」——PM 2026-08-27 的驗算式子本身就是以 59.85
 * 起算（「1階 59.85+1.045=60.895」），等於已拍板報價單要用未進位原值。
 * 代價（已在票上與評論標明）：報價單抽屜的「標準成本」欄改顯示實際代入計算的 59.85，
 * 與產品管理頁看到的 59.9 會差 0.05；這是刻意的，讓使用者能像 PM 那樣自己驗算對得起來。
 *
 * ── PM 2026-08-27 追問三題的結論（R5）
 * Q3「舊資料是指哪些舊資料？」＝ **成本項有 unitPrice、卻拿不到「箱價 ÷ 箱入數」的產品**，
 *   也就是 resolveEntryBaseCost 會落到 fallback 分支的那些。精確定義（兩種都算）：
 *     (a) factoryCost／distributorCosts[i] 的 boxPrice 為空或 ≤ 0，但 unitPrice 有值；
 *     (b) boxPrice 有值，但產品的 pcsPerBox（箱入數）為空或 ≤ 0。
 *    現行三條寫入路徑**都產生不出**這種資料 —— 匯入 productImportParser.js:354/:398 與手動
 *   ProductModal.js:96 的 unitPrice 一律由 calcUnitPrice(boxPrice, pcsPerBox) 推導，缺任一項就
 *   寫 null；productController.js:56 的顯示層補算也是同一式。所以它只可能是 需求 R46
 *   （2026-06-10 標準成本自動推導上線）之前寫入、之後沒再重新匯入過的歷史列，或早年直接改 DB 的列。
 *   實際有幾筆、是哪幾支貨編，用唯讀腳本盤點：
 *     MONGO_URI="..." node src/server/scripts/auditQuoteBaseCostFallback.js
 *   處置（Q3=A，PM 未否決前維持）：這類品項照舊拿已進位的 unitPrice 起算，單價仍可能高 0.1；
 *   要根治只能補齊箱價／箱入數後重新匯入 —— 程式面無法從已進位值還原出原始小數。
 * Q5「已儲存的舊報價單要不要用新公式重算？」＝ **PM 2026-08-27 拍板 A：重算**，
 *   沿用 需求 R104 Q3=B 的既有行為「歷史單開啟編輯頁即以現行公式重算」，舊單開啟就變價。
 *   → 本檔因此**不加**任何版本判斷／舊單凍結分支（需求 R133 的 _legacyCustomCostGuard 只保護
 *     「人工填過自定成本」的列，不是全單凍結）。詳情頁／PDF／Excel 仍讀存值，要等使用者重新
 *     存檔才會同步；PM 已知悉並接受此落差（AC13）。
 * Q「除了台幣，其他幣別是否也會兩次進位？」＝ **會，而且兩個根因都與幣別無關**。
 *   根因 A 發生在「代入哪個成本」，位置在 ÷匯率**之前**；根因 B 的 ceilMoney 依幣別位數。
 *   窮舉箱價 100～3000（每 0.1）× 箱入數 {6,10,12,20,24,30} × 4 個階數 × 材積 {0, 0.7838}
 *   共 1,392,048 組，修正前後單價會不同的比例：
 *     TWD 45.21%　HKD 12.76%　USD 16.04%　EUR 13.70%
 *   （fixture 為了跑得快只取其中的子集 —— 箱入數 {6,12,24}、材積 0、348,012 組 ——
 *     比例分別是 TWD 35.72%／HKD 12.07%／USD 15.19%／EUR 12.66%，量級一致。）
 *   （非 TWD 比例較低只是因為除以匯率後金額變小、跨越進位邊界的機會變少，不是沒中。）
 *   b981707 的修法一次覆蓋四種幣別，本輪以 tests/quoteCurrencyRoundingAudit.fixture.js
 *   逐幣別鎖住。全鏈路（畫面→存檔→詳情頁→PDF→Excel）**確定只進位一次**。
 * Q4（匯入時箱價本身也被進位）：仍採 A「不改」，維持 需求 R70。
 *
 * ── 需求 R140（PM 2026-08-28）：更正「匯出箱價是二次進位」的說法
 * 本檔上一版在此寫「唯一刻意的二次進位是匯出箱價 calcBoxPrice」——**這句話是錯的**。
 * PM 逐字澄清「需求 R111 要解決的是小數位數 2 位，並非要進位 2 次」，查證屬實：
 * quoteMoney.js calcBoxPrice 第二行的 ceilMoney 是浮點雜訊防線（64.1 × 12 ===
 * 769.1999999999999 → 769.2），窮舉 4 幣別 × 2,100,000 組，改變值的次數為 0。
 * 箱價的落差來自「以已進位單價為基準相乘」這個設計選擇，不是進位兩次；詳見
 * quoteMoney.js calcBoxPrice 檔頭與 tests/quoteBoxPriceRounding.fixture.js。
 *
 * ── 需求 R165（PM 2026-09-07）Q1=A：匯出箱價改回「印出來的單價 × 箱入數」
 * PM 逐字拍板「Q1=A（其餘採上述建議即可」，理由是使用者拿匯出上印的單價乘箱入數必須驗算
 * 得出來（0.72*12=8.64、0.82*12=9.84、0.49*24=11.76）。→ **下面那段 需求 R140 Q1=B 已被
 * 改判**：兩支匯出不再呼叫 resolveBoxUnitPriceBase，改把 item.unitPrice 直接交給
 * quoteMoney.js calcBoxPrice（該函式內部先套與單價欄同一個 ceilMoney 再乘）。
 * 本檔的 calcUnitPriceRaw／resolveBoxUnitPriceBase 一律保留（見各自檔頭），單價算式本身
 * 一個字都沒動 —— 本票只換掉「箱價乘誰」。
 *
 * ── 需求 R140（PM 2026-08-28）Q1=B：匯出箱價改以未進位單價算（**已於 需求 R165 被改判**）──
 * 「Q1（更正前提後重問）：B＝以未進位單價算。」→ 推翻 需求 R111 Q3=A 的「以已進位單價
 * 為基準相乘」。實作：本檔新增 calcUnitPriceRaw（roundPrice 之前的原值）與
 * resolveBoxUnitPriceBase（重算 raw + 回頭核對存值，核對不過就退回存值），
 * quoteMoney.js calcBoxPrice 改成「輸入即未進位值、乘完只進位一次」。
 * 只有匯出的**箱價**變動；印出來的單價、詳情頁小計一律仍讀 DB 存值（Q2=A 不變）。
 *
 * ── 需求 R140（PM 2026-08-28）Q2=A：已存檔舊單不主動回寫
 * 「已存檔的非台幣舊報價單要不要主動回寫成新公式？ A＝不動，維持『開編輯頁重存才更新』。」
 * → 不寫批次回寫腳本、詳情頁／PDF／Excel 一律讀 DB 存值不重算（三處檔頭已標明此落差）。
 * ── 需求 R140（PM 2026-08-28）Q5=B：fallback 品項不加畫面提示
 * 「是否需要在報價單畫面標示『此品項成本為估算值』？ B＝不需要。」
 * → 不新增任何提示 UI；此決議以 tests/quoteBoxPriceRounding.fixture.js [5] 反向鎖住。
 *
 * ── 需求 R145（PM 2026-08-29，Q1~Q9 全 A）：冷凍品項的材積係數 23
 * 「系列歸類在『冷凍品項』『冷凍散裝原料』的商品在報價單中的階數有獨立公式（一樣四階）。」
 * 差異只有一處＝材積係數 16 → 23（Q2=A），四階除數不變；實作見下方 calcTierPrice 與
 * FROZEN_SERIES／getVolumeFactor 的註解。本票對本檔其餘規則零影響：
 *   ‧ 成本基準仍是 resolveEntryBaseCost 的未進位原值（AC9），中途不多做一次進位。
 *   ‧ 幣別換算時機不變（AC10）：23×材積÷箱入數 屬 TWD，與標準成本一起除匯率一次。
 *   ‧ 全鏈路仍只在 calcUnitPrice 末端進位一次（AC11，D4／ceilMoney 不變）。
 *   ‧ isManualPrice／_legacyCustomCostGuard／全單加成／單品加價／貼標費用順序不變（AC12）。
 * Q5=A（已存檔舊單）： **冷凍品的舊報價單一開編輯頁就會以新公式重算而變價**（單價變高，
 *   因為材積成本由 16 倍改成 23 倍）。詳情頁／PDF／Excel 仍讀 DB 存值不重算，要等使用者
 *   在編輯頁重新存檔才會同步——與 需求 R132 Q5=A／需求 R140 Q2=A 的既有慣例一致，
 *   本檔因此**不加**任何「舊單凍結」版本分支。
 * Q7=A／Q9=A：不加任何提示 UI，也不在抽屜／欄位標示「此品項套用冷凍公式」。
 */

// 顯式 `.js` 副檔名：讓 tests/*.fixture.js 走 Node 原生 ESM loader（loadESMFromCJS）
// require('./quoteUnitPrice') 時能解析到相鄰的 quoteMoney.js。webpack `resolve.extensions`
// 已含 `.js`，顯式副檔名對正式建置無副作用（webpack 5、babel-transform-modules-commonjs 均支援）。
import { ceilMoney } from './quoteMoney.js';

/**
 * 成本基準的精度上限。
 * 箱價 ÷ 箱入數 在 JS 裡會帶二進位尾巴（718.2 / 12 === 59.849999999999994），
 * 直接拿去顯示會變成一長串。先收到小數 6 位＝把尾巴抹掉、還原成使用者心中的 59.85，
 * 對「無條件進位到小數 1～2 位」的最終結果不可能有影響（差距 1e-6 量級）。
 *  這**不是**進位到成本，只是清除浮點雜訊；真正的「進位」只發生在 roundPrice 一次。
 */
const COST_PRECISION = 6;

function cleanCost(n) {
  if (!Number.isFinite(n)) return null;
  return Number(n.toFixed(COST_PRECISION));
}

/**
 * 需求 R74：成本來源 costSource 有兩種寫法——新版帶索引 distributor_0／distributor_1，
 * 舊版／匯入前為無索引 'distributor'（遺失是第 1 或第 2 筆）。報價單端一律把 legacy
 * 'distributor' 正規化視同 'distributor_0'，與 ProductDetailPage 載入映射一致。factory 不變。
 */
export function normalizeCostSource(costSource) {
  return costSource === 'distributor' ? 'distributor_0' : costSource;
}

/**
 * 需求 R132 主修正：由箱價 ÷ 箱入數取得**未進位**的標準成本。
 * 對照 server/utils/cost.js calcUnitPrice ＝ 同一條除法，差別只在那邊多包一層 Math.ceil。
 */
export function baseCostFromBox(boxPrice, pcsPerBox) {
  const bp = Number(boxPrice);
  const pcs = Number(pcsPerBox);
  if (!Number.isFinite(bp) || !Number.isFinite(pcs)) return null;
  if (bp <= 0 || pcs <= 0) return null;
  return cleanCost(bp / pcs);
}

/**
 * 單一成本項（factoryCost／distributorCosts[i]）的計算基準。
 *
 * 優先序是本票的核心：**先箱價 ÷ 箱入數（未進位原值），拿不到才回落 unitPrice**。
 * 舊版剛好相反（unitPrice 優先），而 DB 裡的 unitPrice ＝ ceil(箱價 ÷ 箱入數)，
 * 於是報價單永遠拿到被墊高過的成本 —— 就是 PM 回報 61.0／66.2 的來源。
 *
 * 反向安全性：ProductModal 的「成本金額(單價)」欄位是 disabled 的自動欄
 * （ProductModal.js:1082-1088 `value={fmtMoney(entry.unitPrice)}` + disabled），
 * 值一律由 calcUnitPrice(boxPrice, pcsPerBox) 產生，沒有「人工填了一個與箱價無關的單價」
 * 這種資料，所以優先採箱價不會蓋掉任何人工輸入。
 *
 *  Q3=A fallback（PM 2026-08-27 追問「舊資料是指哪些」，定義見檔頭）：
 * 「boxPrice 空／≤0」或「pcsPerBox 空／≤0」的成本項無法還原未進位原值，只能沿用已進位的
 * unitPrice —— **該類品項的單價仍可能比手算高一個進位單位**，是本票已知且刻意保留的邊界。
 *
 *  需求 R140 AC10（全幣別影響，勿誤讀成「只有台幣會中」）：這條 fallback 位在
 * calcUnitPrice 的 `÷ 匯率` **之前**（下方 calcUnitPrice 情境一／情境二皆然），代入的是
 * 已被墊高的 TWD 成本，墊高量會跟著除以匯率一路傳到最終單價，因此
 * **HKD／USD／EUR 一樣會高一個進位單位**，只是「一個進位單位」依幣別位數不同：
 *   TWD／HKD ＝ 0.1（quoteMoney.js DEFAULT_MONEY_DECIMALS）
 *   USD／EUR ＝ 0.01（MONEY_DECIMALS_BY_CURRENCY）
 * 程式面無法從已進位值還原原始小數，只能補齊箱價／箱入數後重新匯入。
 * 現行寫入路徑產生不出這種資料（見檔頭），只會出現在 需求 R46 之前的歷史列；
 * 要盤點有哪些：node src/server/scripts/auditQuoteBaseCostFallback.js（唯讀）。
 *
 * PM 2026-08-28（需求 R140 Q5=B）：這類品項**不需要**在報價單畫面上標示「成本為估算值」，
 * 因此本檔與兩頁都不加任何提示 UI；補件前的處置就是照舊沿用已進位值。
 */
export function resolveEntryBaseCost(entry, pcsPerBox) {
  if (!entry) return null;
  const fromBox = baseCostFromBox(entry.boxPrice, pcsPerBox);
  if (fromBox != null) return fromBox;
  if (entry.unitPrice != null && entry.unitPrice !== '') {
    const n = Number(entry.unitPrice);
    if (Number.isFinite(n)) return n; // Q3=A fallback：已進位值，可能高 0.1
    return null;
  }
  return null;
}

/**
 * 需求 R170（PM 2026-09-15 拍板 Q1=A／Q3=A）：依成本來源取出該產品的
 * **成本項本體**（factoryCost 或 distributorCosts[idx]），沒有就回 null。
 *
 * 為什麼要多這一層：報價單「加入產品」原本 costSource／baseCost 已經依
 * normalizeCostSource(p.costSource) 分流，唯獨 MOQ 寫死 `p.factoryCost?.moq` ——
 * 產品勾「經銷1 使用此成本報價」時，列上的成本是經銷的、MOQ 卻是工廠的（來源與 MOQ 錯配）。
 * 兩頁 handleAddProducts 與 handleCostSourceChange 一律改走這裡取 entry，
 * getBaseCostFromSource 也只是在它之上再做「箱價 ÷ 箱入數」，日後新增成本來源只會有一個落點。
 *
 * ‧ legacy 'distributor'（無索引）經 normalizeCostSource 視為 distributor_0（需求 R74）。
 * ‧ 未知來源／索引越界／product 為空 → null；呼叫端取 `?.moq || ''` 就是 Q3=A「顯示空字串」，
 *   **不得**在這裡回退到 factoryCost（那正是本票要修掉的錯配）。
 * ‧ Q1=A：既有已存報價單的 item.moq 不回填（新單起才生效）——本 helper 只在「加入產品」
 *   與「抽屜切換來源」兩個入口被呼叫，載入既有單時不會重算 MOQ。
 */
export function getCostEntryFromSource(product, costSource) {
  if (!product) return null;
  const src = normalizeCostSource(costSource || 'factory');
  if (src === 'factory') return product.factoryCost ?? null;
  if (typeof src === 'string' && src.startsWith('distributor_')) {
    const idx = parseInt(src.split('_')[1], 10);
    if (!Number.isInteger(idx) || idx < 0) return null;
    return product.distributorCosts?.[idx] ?? null;
  }
  return null;
}

/** 依成本來源取出該產品的標準成本（未進位原值）。兩頁共用，不得再各留一份。 */
export function getBaseCostFromSource(product, costSource) {
  if (!product) return null;
  const entry = getCostEntryFromSource(product, costSource);
  if (!entry) return null;
  return resolveEntryBaseCost(entry, product.pcsPerBox);
}

/**
 * ── 需求 R149（PM 2026-08-29 拍板 Q1~Q10 全 A）：自訂**箱**成本 → 包成本 ──
 *
 * PM 原文：「新增『使用自訂箱成本換算』：該欄位用途與現況的『使用自定成本換算』相同，
 * 但是它會需要『先經由』換算得出包成本。」
 *
 * Q2=A：包成本 ＝ 自訂箱成本 ÷ item.pcsPerBox（箱入數）—— 與 baseCostFromBox() 是**同一條除法**
 * （箱價 ÷ 箱入數），差別只在被除數是使用者手填的箱成本而不是產品主檔的箱價。因此這裡刻意
 * **委派**給 baseCostFromBox 而不是再抄一次算式：兩者日後若要改精度／改邊界，只會有一個落點。
 *
 * Q3=A：**中途不進位**，只用 cleanCost（toFixed(6)）清浮點雜訊 —— 例如 350 ÷ 3 = 116.66666…
 * 收成 116.666667 後原樣進階數公式，全鏈路仍然只在最終單價無條件進位一次
 * （守 需求 R132「兩次進位」的修正，別在這裡自作聰明先 ceil 到小數 1 位）。
 *
 * Q4=A：箱入數空／≤0 時回傳 null（換算不出包成本）。兩頁據此把勾選框 disabled 並顯示
 * 「此品項未填箱入數，無法用箱成本換算」；getEffectiveBaseCost 這一側也會落回來源成本，
 * 因此就算有人繞過 UI 也不會算出 NaN／Infinity 單價（AC10）。
 *
 *  為什麼不做成「直接覆寫箱價」：系統根本沒有獨立的箱價成本欄位，匯出箱價是**從單價回推**
 * 的（quoteMoney.js calcBoxPrice(item.unitPrice × pcsPerBox)，需求 R165 Q1=A；需求 R140 Q1=B
 * 時代則是乘 resolveBoxUnitPriceBase 取得的未進位原值）。
 * 唯一能讓箱價變動的正確路徑，就是把箱成本換算回包成本、接回 item.baseCost，讓箱價自然跟著走。
 */
export function packCostFromCustomBoxCost(boxCost, pcsPerBox) {
  return baseCostFromBox(boxCost, pcsPerBox);
}

/**
 * 需求 R149 Q4=A：這個品項能不能用「自訂箱成本」＝有沒有可用的箱入數。
 * 兩頁的勾選框 disabled 條件與 handler 防線都讀這一個判斷，避免各寫一份而漂移。
 */
export function canUseCustomBoxCost(item) {
  const pcs = Number(item?.pcsPerBox);
  return Number.isFinite(pcs) && pcs > 0;
}

/** Q4=A 指定的逐字文案（兩頁共用，改字只會有一個落點）。 */
export const CUSTOM_BOX_COST_NO_PCS_HINT = '此品項未填箱入數，無法用箱成本換算';

/**
 * 需求 R32：自定成本啟用且值為有限正數時，以自填成本覆寫來源成本；
 * 其餘情況（未啟用 / 0 / 空 / NaN / Infinity / 負數）維持來源成本，避免單價失準或變成 NaN。
 * 需求 R132 AC9：自定成本是使用者手填值，原樣進算式、中途不進位，行為不變。
 *
 * 需求 R149：新增第二種自訂成本（自訂**箱**成本）。這裡是**整個功能唯一的覆寫點**，
 * 兩頁共用；箱→包的換算也只准寫在這裡，禁止在 QuoteCreatePage／QuoteEditPage 各留一份
 * （AC8，需求 R132 收斂兩頁複製品的教訓）。
 *
 * Q1=A 互斥：兩個自訂成本同時只有一個能生效，互斥由兩頁的 handler 維護
 * （勾其一自動取消另一）。下面的先後順序是**防禦性**的：正常資料走不到「兩個旗標都為真」，
 * 萬一有髒資料（例如手改 DB）也要有確定性結果 —— 選「自訂包成本優先」＝與本票之前的行為
 * 完全一致，讓舊資料的單價不會因為本票而變動（AC2 零回歸）。
 */
export function getEffectiveBaseCost(item) {
  const sourceCost = getBaseCostFromSource(item._productData, item.costSource);
  const n = Number(item.customCostValue);
  if (item.customCostEnabled && Number.isFinite(n) && n > 0) {
    return n;
  }
  if (item.customBoxCostEnabled) {
    const packCost = packCostFromCustomBoxCost(item.customBoxCostValue, item.pcsPerBox);
    // Q4=A：換算不出包成本（箱入數缺漏／箱成本非正數）就落回來源成本，絕不回傳 null 或 NaN。
    if (packCost != null) return packCost;
  }
  return sourceCost;
}

/** 抽屜「標準成本」顯示用：把 59.849999999999994 這種尾巴收乾淨再印。 */
export function formatBaseCost(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return String(Number(n.toFixed(4)));
}

/**
 * ── 需求 R145（PM 2026-08-29 拍板 Q1~Q9 全 A）：冷凍商品的材積係數 ──
 *
 * PM 原文：「系列歸類在『冷凍品項』『冷凍品散裝原料』的商品在報價單中的階數有獨立公式
 * （一樣四階）：1階 標準成本 + 23×材積÷箱入數、2階 標準成本÷0.975 + 23×材積÷箱入數、
 * 3階 ÷0.95、4階 ÷0.92。沒有被分類在這兩個系列的品項維持原算式。」
 *
 * Q2=A 確認：**冷凍公式與現行公式的唯一差異就是材積係數 16 → 23**，四階除數
 * 1 / 0.975 / 0.95 / 0.92 完全相同。因此這裡不是「第二條公式」，而是同一條公式的
 * 材積係數依系列切換 —— 刻意不複製一份 calcTierPriceFrozen，避免兩份算式日後漂移
 * （需求 R132 收斂兩頁複製品的教訓）。
 *
 * Q1=A 名單：只有「冷凍品項」與「冷凍散裝原料」兩個系列。
 *    PM 原文寫的「冷凍品散裝原料」在系統中不存在，src/server/config/db.js SERIES_OPTIONS
 *      的正確字串是「冷凍散裝原料」（少一個「品」字），PM 已於 Q1=A 確認以系統字串為準。
 *    另外 4 個以「冷凍」開頭的系列（冷凍品項(進行中)／冷凍業務品(進行中)／
 *      冷凍生鮮海產(進行中)／冷凍自有品牌(進行中)）**不在名單內**（Q1 選 A 而非 B／C），
 *      因此本檔用「完全比對」而不是 startsWith('冷凍')；未來要納入須由 PM 另行拍板。
 * Q8=A：23 寫死在程式（同現行 16），不做成後台可調參數。
 *
 * 伺服器端的同一份名單在 src/server/utils/frozenSeries.js（產品匯出階梯價 Q4=A 也要 23）。
 * 前端無法 require 後端檔案（CRA 編譯範圍限 src/client），兩份必須逐字一致，
 * 由 tests/quoteFrozenSeriesTier.fixture.js 比對鎖住。
 */
export const FROZEN_SERIES = ['冷凍品項', '冷凍散裝原料'];
/** 一般品項的材積係數（需求 R82 D1 起沿用至今，本票一字未動）。 */
export const VOLUME_FACTOR_DEFAULT = 16;
/** 冷凍品項的材積係數（需求 R145 Q2=A：與一般品的唯一差異）。 */
export const VOLUME_FACTOR_FROZEN = 23;

export function isFrozenSeries(series) {
  if (series == null) return false;
  return FROZEN_SERIES.includes(String(series).trim());
}

/** 依系列取材積係數：冷凍 23、其餘（含空值／未知系列）一律 16。 */
export function getVolumeFactor(series) {
  return isFrozenSeries(series) ? VOLUME_FACTOR_FROZEN : VOLUME_FACTOR_DEFAULT;
}

/**
 * 取這一列品項的系列（需求 R145 Q6=A：以「加入報價單當下」的快照為準）。
 *
 * 三層 fallback，順序即優先序：
 *   1. item.series ── 存進 Quote.items 的快照（Quote.js quoteItemSchema，比照 shelfLife/需求 R121）。
 *      Q6=A「之後產品改系列不影響已建立的報價單」＝快照優先，不被實時值蓋掉。
 *   2. item._productData?.series ── 兩頁抽屜用的實時產品資料（建立頁來自產品列表 API、
 *      編輯頁來自載入後補抓 GET /products/:id）。只服務「本票上線前存的舊單」——它們沒有快照。
 *   3. item.product?.series ── getById 的 .populate('items.product', '... series ...')。
 *      這一層是 AC8 的關鍵：編輯頁剛載入時 _productData 仍是 null（QuoteEditPage.js:570），
 *      舊單若只靠第 2 層，空窗期內任何重算都會把冷凍品誤算成 16。populate 值在第一次
 *      setItems 就到手，空窗期因此不存在。
 */
export function resolveItemSeries(item) {
  if (!item) return '';
  if (item.series != null && item.series !== '') return item.series;
  const fromProductData = item._productData?.series;
  if (fromProductData != null && fromProductData !== '') return fromProductData;
  const populated = typeof item.product === 'object' ? item.product?.series : null;
  if (populated != null && populated !== '') return populated;
  return '';
}

/**
 * 階數係數：1 階不打折，2/3/4 階分別 ÷0.975 / ÷0.95 / ÷0.92；
 * 材積成本 ＝ 材積係數 × 材積 ÷ 箱入數，係數依系列而定（一般 16、冷凍 23，需求 R145）。
 *
 * 第 5 個參數 series 為**選用**且預設非冷凍：既有的四參呼叫與既有 fixture 的
 * 「16×材積÷箱入數」斷言原樣通過（需求 R145 AC6），冷凍分支另立 fixture 鎖住。
 */
export function calcTierPrice(baseCost, tier, volumeCuft, pcsPerBox, series) {
  const vol = (volumeCuft || 0);
  const pcs = (pcsPerBox || 1);
  const volCost = getVolumeFactor(series) * vol / pcs;
  if (tier === 1) return baseCost + volCost;
  if (tier === 2) return baseCost / 0.975 + volCost;
  if (tier === 3) return baseCost / 0.95 + volCost;
  if (tier === 4) return baseCost / 0.92 + volCost;
  return baseCost + volCost;
}

export function applySpecialFormula(price, formula) {
  if (!formula || !formula.trim()) return price;
  // parse sequential operators, e.g. "*1.03/3.8"
  const tokens = formula.trim().match(/[*/+-][0-9.]+/g);
  if (!tokens) return price;
  let result = price;
  for (const token of tokens) {
    const op = token[0];
    const num = parseFloat(token.slice(1));
    if (isNaN(num)) continue;
    if (op === '*') result *= num;
    else if (op === '/') result /= num;
    else if (op === '+') result += num;
    else if (op === '-') result -= num;
  }
  return result;
}

/**
 * 單價無條件進位（TWD／HKD 1 位、USD／EUR 2 位，規則與 需求 R111 Q1/Q2 相同）。
 *
 * 需求 R132 根因 B：舊版兩頁各自寫死 Math.ceil(price * 10) / 10，沒有浮點雜訊防線，
 * 11.4 / 0.95 === 12.000000000000002 會被進位成 12.1。改走 quoteMoney.js 的 ceilMoney，
 * 它會先 Number((v * factor).toFixed(6)) 把 1e-13 等級誤差抹掉再 ceil。
 * ceilMoney 對非數字回 null，這裡原樣退回輸入值以維持舊有的 NaN 傳遞行為。
 */
export function roundPrice(price, currency) {
  const ceiled = ceilMoney(price, currency);
  return ceiled === null ? price : ceiled;
}

export function formatPrice(price, currency) {
  if (price == null || price === '') return '';
  if (currency === 'USD' || currency === 'EUR') return Number(price).toFixed(2);
  return Number(price).toFixed(1);
}

// 需求 R82（PM 2026-07-22 拍板 D1~D4）：報價單單價運算重定義。
// - 情境一 特殊公式「有值」：「價格階數」直接失效不套用；單價 = 標準成本 →（套用特殊公式）
// → 全單加成 → 單品加價 → 貼標費用。
// - 情境二 特殊公式「為空」：維持階數運算；單價 = 標準成本 → 階數 → 全單加成 → 單品加價 → 貼標費用。
// - D1 材積成本：特殊公式生效、階數停用時「純以標準成本進特殊公式」，不加材積成本(16×材積/箱入數)。
// - D2 加成順序：「全單加成 → 單品加價」（先套百分比乘法，再加單品加價）。
// - D3 幣別匯率（需求 R83 PM 2026-07-24 缺陷修正——換算「時機」）：非 TWD 幣別時，只在
// 「TWD 成本基礎」全部算完後換算一次。材積成本 16×材積/箱入數 屬 TWD 計價，若換算置於
// 階數之前，材積成本會被直接加到目標幣別單價上而未一起換算（PM 案例：USD 30.8、成本 80、
// 材積 1.19，預期 (80+1.19)/30.8=2.636→2.64，錯誤算成 80/30.8+1.19=3.79）。
// ‧ 情境二（階數/材積）：在 TWD 上算完標準成本+階數+材積 → 再 ÷匯率 一次。
// ‧ 情境一（特殊公式）：維持先 ÷匯率 再套公式（公式為非匯率純係數、順序不影響結果）。
// ‧ 單品加價 itemMarkup、貼標費用 labelingFee 本就以目標幣別儲存，直接相加即可。
// - D4 進位時機：只在最後換算結果進位一次。
// isManualPrice（手動單價）維持最優先。
// 需求 R132 AC11：情境一與情境二的「標準成本」輸入是同一個 item.baseCost，
// 而 baseCost 一律出自 getEffectiveBaseCost → getBaseCostFromSource（未進位原值），
// 兩條路徑不可能一條用未進位、一條用已進位。
export function calcUnitPrice(item, exchangeRate, specialFormula, currency, globalMarkupPct) {
  if (item.isManualPrice) return item.unitPrice;
  // 需求 R133 Q3=A：舊單保護中的列一律保留存檔單價（比照 isManualPrice 早退）。
  // 編輯頁所有自動重算最後都會走到這裡，在此一處早退即可保證「人沒動過的舊單不會因為
  // 任何連鎖重算而無聲變價」。建立頁的品項不會有這個旗標，等同 no-op。
  if (item._legacyCustomCostGuard) return item.unitPrice;
  const raw = calcUnitPriceRaw(item, exchangeRate, specialFormula, currency, globalMarkupPct);
  if (raw == null) return null;
  // D4：只在最後換算結果進位一次（需求 R132 根因 B：改走 ceilMoney，含浮點雜訊防線）。
  return roundPrice(raw, currency || 'TWD');
}

/**
 * 同一條算式的「未進位原值」（需求 R140 Q1=B 新增）。
 *
 * 為什麼要把它獨立出來：PM 2026-08-28 拍板「匯出箱價以未進位單價算」，匯出層需要的是
 * roundPrice **之前**的那個值。過去這個值只活在 calcUnitPrice 的區域變數裡、算完就丟，
 * 匯出層只能拿 DB 存的已進位單價去乘箱入數，箱價因此被墊高（HKD 每箱可差 1.0 元）。
 *
 *  本函式**不含** isManualPrice／_legacyCustomCostGuard 兩道早退（那兩道仍只存在於
 * calcUnitPrice，AC9／需求 R132 5d 的反向鎖數的就是它們）。原因：那兩種列的「單價」是
 * 使用者手填值或凍結的存檔值，根本不是這條算式的產物，硬算一個 raw 出來反而危險。
 * 呼叫端要處理這兩種列時，一律走下方 resolveBoxUnitPriceBase（它會直接退回存值）。
 */
export function calcUnitPriceRaw(item, exchangeRate, specialFormula, currency, globalMarkupPct) {
  const baseCost = item.baseCost;
  if (baseCost == null) return null;

  const hasFormula = !!(specialFormula && specialFormula.trim());

  let price = baseCost; // TWD 標準成本（需求 R132：未進位原值）

  if (hasFormula) {
    // 情境一：特殊公式有值 → 階數失效，D1 純以標準成本進特殊公式（不加材積成本）。
    // 需求 R145 Q3=A（PM 2026-08-29）：冷凍品項在這條路徑上**也不加** 23×材積÷箱入數 ——
    // 特殊公式優先、階數與材積成本皆不套用，維持 需求 R82 D1 不變，冷凍公式等於不生效。
    // （反向鎖：tests/quoteFrozenSeriesTier.fixture.js [4]）
    if (currency && currency !== 'TWD') {
      const rate = exchangeRate || 1;
      price = price / rate;
    }
    price = applySpecialFormula(price, specialFormula);
  } else {
    // 情境二：特殊公式為空 → 維持階數運算（含材積成本 係數×材積/箱入數）。
    // 材積成本屬 TWD 計價，必須「連同標準成本一起」換算（需求 R83／需求 R82 D3）。
    // 需求 R145：材積係數由 resolveItemSeries 決定（冷凍 23、其餘 16）；係數換掉不影響
    // 換算時機——÷匯率 仍在整個 TWD 端算完之後才做一次（AC10）。
    price = calcTierPrice(
      price,
      item.priceTier || 1,
      item.volumeCuft,
      item.pcsPerBox,
      resolveItemSeries(item)
    );
    if (currency && currency !== 'TWD') {
      const rate = exchangeRate || 1;
      price = price / rate;
    }
  }

  // D2：加成順序「全單加成 → 單品加價」——先套全單加成百分比（乘法），再加單品加價（加法）。
  if (globalMarkupPct && globalMarkupPct > 0) {
    price = price * (1 + globalMarkupPct / 100);
  }
  price += (item.itemMarkup || 0);

  // 貼標費用以目標幣別儲存，於幣別換算後加入。
  price += (item.labelingFee || 0);

  // 這裡**不進位**——進位是 calcUnitPrice 的最後一步（D4：全鏈路只進位一次）。
  return price;
}

/**
 * 【需求 R165（PM 2026-09-07 Q1=A）起，本函式已不在任何匯出路徑上 —— 保留、勿刪】
 *   PM 改判「箱價＝匯出上印出來的單價 × 箱入數」（0.72*12=8.64），quotePdfExport／
 *   quoteExcelExport 兩支都改成直接把 item.unitPrice 交給 calcBoxPrice，不再呼叫這裡。
 *   函式本身完整保留：① tests/quoteFrozenSeriesTier.fixture.js 等既有測試仍以它
 *   驗證 calcUnitPriceRaw ＋ roundPrice 的一致性；② 它是「未進位原值可被重算並回頭核對」
 *   這件事的唯一實作，日後若 PM 又要回到 Q1=B 就直接接回去。
 *    不要因為「production 沒有呼叫端」而刪掉它，也不要重新接回匯出層（那會推翻 Q1=A）。
 *
 * ── 以下為 需求 R140 Q1=B 時代的原始說明（沿革，勿刪）
 * 匯出箱價要乘的那個「單價基準」（需求 R140 Q1=B，PM 2026-08-28）。
 *
 * PM 逐字：「Q1（更正前提後重問）：B＝以未進位單價算。」
 * 回傳未進位原值給 quoteMoney.js calcBoxPrice 相乘；拿不到時安全退回 DB 存值。
 *
 * ── 為什麼是「重算 raw + 回頭核對」而不是直接重算
 * DB 只存了已進位的 item.unitPrice（schema 沒有未進位欄位），未進位原值只能由同一批
 * 快照欄位（baseCost／priceTier／volumeCuft／pcsPerBox／itemMarkup／labelingFee ＋
 * 單頭的 exchangeRate／specialFormula／globalMarkup）重算。但 需求 R140 Q2=A 明訂
 * 「已存檔的舊單不主動回寫、不即時重算單價」，所以這裡加一道核對：
 *   roundPrice(raw) 必須等於存檔的 unitPrice，才採用 raw。
 * 不相等 ＝ 這張單的存值不是現行公式的產物（需求 R132 之前存的舊單、缺快照欄位的更舊
 * 資料、或人為改過 DB）→ 一律退回存值，**箱價維持舊行為，畫面單價一個字都不會變**。
 * 換句話說：本函式只在「重算結果與存值完全一致」時才把精度還原回去，不可能因此改動
 * 任何一張單印出來的單價（AC4／Q2=A）。
 *
 * 兩種列直接退回存值、不重算：
 *   ‧ isManualPrice：單價是業務手打的（可能不在幣別刻度上，如 60.85），箱價必須跟著它。
 *   ‧ _legacyCustomCostGuard（需求 R133 Q3=A）：凍結中的舊單列，任何重算都不得套用。
 */
export function resolveBoxUnitPriceBase(item, quote) {
  if (!item) return null;
  const stored = item.unitPrice;
  if (stored == null || stored === '') return stored ?? null;
  if (item.isManualPrice || item._legacyCustomCostGuard) return stored;

  const currency = quote?.currency || 'TWD';
  const raw = calcUnitPriceRaw(
    item,
    quote?.exchangeRate,
    quote?.specialFormula,
    currency,
    quote?.globalMarkup
  );
  if (raw == null || !Number.isFinite(Number(raw))) return stored;
  if (roundPrice(raw, currency) !== Number(stored)) return stored;
  return raw;
}
